import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
function LegalModal({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      const handleEsc = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose();
      };
      window.addEventListener('keydown', handleEsc);
      return () => {
        document.body.style.overflow = '';
        window.removeEventListener('keydown', handleEsc);
      };
    }
  }, [isOpen, onClose]);
  return <AnimatePresence data-id="element-48">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8" role="dialog" aria-modal="true" aria-label={title} data-id="element-49">
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-[#2C2825]/50 backdrop-blur-sm" onClick={onClose} data-id="element-50" />

          {/* Modal content */}
          <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 10,
        scale: 0.98
      }} transition={{
        duration: 0.25,
        ease: 'easeOut'
      }} className="relative w-full max-w-2xl max-h-[80vh] bg-[#FAF7F2] rounded-2xl shadow-2xl shadow-[#2C2825]/15 overflow-hidden" data-id="element-51">
            {/* Header */}
            <div className="sticky top-0 z-10 bg-[#FAF7F2] border-b border-[#B8A99A]/20 px-6 py-4 flex items-center justify-between" data-id="element-52">
              <h2 className="text-lg font-semibold text-[#2C2825]" style={{
            fontFamily: "'Playfair Display', serif"
          }} data-id="element-53">
                {title}
              </h2>
              <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full text-[#2C2825]/50 hover:text-[#2C2825] hover:bg-[#2C2825]/5 transition-colors" aria-label="Cerrar" data-id="element-54">
                <XIcon className="w-5 h-5" data-id="element-55" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-6 overflow-y-auto max-h-[calc(80vh-64px)] text-[#2C2825]/75 text-sm leading-relaxed space-y-4" data-id="element-56">
              {children}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
function PrivacyContent() {
  return <>
      <p className="text-xs text-[#2C2825]/40 uppercase tracking-wider font-semibold" data-id="element-57">
        Última actualización: febrero 2026
      </p>

      <h3 className="text-base font-semibold text-[#2C2825] pt-2" data-id="element-58">
        1. Responsable del tratamiento
      </h3>
      <p data-id="element-59">
        El responsable del tratamiento de tus datos personales es Mapa de
        Ojeras™. Al adquirir nuestro producto digital, aceptas las condiciones
        descritas en esta política.
      </p>

      <h3 className="text-base font-semibold text-[#2C2825] pt-2" data-id="element-60">
        2. Datos que recopilamos
      </h3>
      <p data-id="element-61">
        Recopilamos únicamente los datos necesarios para procesar tu compra y
        entregarte el producto:
      </p>
      <ul className="list-disc pl-5 space-y-1" data-id="element-62">
        <li data-id="element-63">Dirección de correo electrónico (para envío del producto)</li>
        <li data-id="element-64">
          Datos de pago (procesados de forma segura por nuestro proveedor de
          pagos)
        </li>
        <li data-id="element-65">Datos de navegación anónimos (cookies analíticas)</li>
      </ul>

      <h3 className="text-base font-semibold text-[#2C2825] pt-2" data-id="element-66">
        3. Finalidad del tratamiento
      </h3>
      <p data-id="element-67">Tus datos se utilizan exclusivamente para:</p>
      <ul className="list-disc pl-5 space-y-1" data-id="element-68">
        <li data-id="element-69">Procesar y entregar tu compra</li>
        <li data-id="element-70">Enviarte el producto digital por email</li>
        <li data-id="element-71">Atender consultas relacionadas con tu pedido</li>
      </ul>

      <h3 className="text-base font-semibold text-[#2C2825] pt-2" data-id="element-72">
        4. Conservación de datos
      </h3>
      <p data-id="element-73">
        Conservamos tus datos durante el tiempo necesario para cumplir con las
        finalidades descritas y las obligaciones legales aplicables.
      </p>

      <h3 className="text-base font-semibold text-[#2C2825] pt-2" data-id="element-74">
        5. Tus derechos
      </h3>
      <p data-id="element-75">
        Puedes ejercer tus derechos de acceso, rectificación, supresión,
        oposición y portabilidad escribiéndonos por email. Responderemos en un
        plazo máximo de 30 días.
      </p>

      <h3 className="text-base font-semibold text-[#2C2825] pt-2" data-id="element-76">
        6. Cookies
      </h3>
      <p data-id="element-77">
        Utilizamos cookies estrictamente necesarias para el funcionamiento del
        sitio y cookies analíticas para mejorar tu experiencia. Puedes gestionar
        tus preferencias desde la configuración de tu navegador.
      </p>
    </>;
}
function TermsContent() {
  return <>
      <p className="text-xs text-[#2C2825]/40 uppercase tracking-wider font-semibold" data-id="element-78">
        Última actualización: febrero 2026
      </p>

      <h3 className="text-base font-semibold text-[#2C2825] pt-2" data-id="element-79">1. Objeto</h3>
      <p data-id="element-80">
        Estos términos regulan la compra y uso del producto digital "Mapa de
        Ojeras™", una guía descargable en formato digital.
      </p>

      <h3 className="text-base font-semibold text-[#2C2825] pt-2" data-id="element-81">
        2. Producto digital
      </h3>
      <p data-id="element-82">
        Al completar la compra, recibirás acceso al producto digital a través de
        un enlace de descarga enviado a tu correo electrónico. El producto se
        entrega tal cual, sin garantía de resultados específicos, ya que estos
        dependen de factores individuales.
      </p>

      <h3 className="text-base font-semibold text-[#2C2825] pt-2" data-id="element-83">
        3. Precio y pago
      </h3>
      <p data-id="element-84">
        El precio del producto es el indicado en la página de venta en el
        momento de la compra. El pago se realiza de forma única (no es una
        suscripción) a través de nuestra plataforma de pago segura.
      </p>

      <h3 className="text-base font-semibold text-[#2C2825] pt-2" data-id="element-85">
        4. Política de devoluciones
      </h3>
      <p data-id="element-86">
        Dado que se trata de un producto digital de descarga inmediata, no se
        admiten devoluciones una vez realizada la entrega, salvo en los casos
        previstos por la legislación vigente.
      </p>

      <h3 className="text-base font-semibold text-[#2C2825] pt-2" data-id="element-87">
        5. Propiedad intelectual
      </h3>
      <p data-id="element-88">
        Todo el contenido del producto está protegido por derechos de propiedad
        intelectual. La compra otorga una licencia de uso personal e
        intransferible. Queda prohibida la reproducción, distribución o reventa
        del contenido.
      </p>

      <h3 className="text-base font-semibold text-[#2C2825] pt-2" data-id="element-89">
        6. Limitación de responsabilidad
      </h3>
      <p data-id="element-90">
        El contenido del producto tiene carácter informativo y educativo. No
        sustituye el consejo médico profesional. Consulta con un especialista si
        tienes dudas sobre tu salud.
      </p>

      <h3 className="text-base font-semibold text-[#2C2825] pt-2" data-id="element-91">
        7. Legislación aplicable
      </h3>
      <p data-id="element-92">
        Estos términos se rigen por la legislación española y europea de
        protección al consumidor.
      </p>
    </>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="w-full px-5 py-8 border-t border-[#B8A99A]/15" data-id="element-93">
        <div className="max-w-3xl mx-auto flex flex-col items-center gap-3" data-id="element-94">
          <p className="text-sm text-[#2C2825]/40" data-id="element-95">
            © 2026 Mapa de Ojeras™ · Producto digital
          </p>
          <div className="flex items-center gap-4" data-id="element-96">
            <button onClick={() => setActiveModal('privacy')} className="text-xs text-[#2C2825]/40 hover:text-[#C4956A] transition-colors underline underline-offset-2 decoration-[#2C2825]/15 hover:decoration-[#C4956A]/40" data-id="element-97">
              Política de Privacidad
            </button>
            <span className="text-[#2C2825]/15" data-id="element-98">·</span>
            <button onClick={() => setActiveModal('terms')} className="text-xs text-[#2C2825]/40 hover:text-[#C4956A] transition-colors underline underline-offset-2 decoration-[#2C2825]/15 hover:decoration-[#C4956A]/40" data-id="element-99">
              Términos y Condiciones
            </button>
          </div>
        </div>
      </footer>

      <LegalModal isOpen={activeModal === 'privacy'} onClose={() => setActiveModal(null)} title="Política de Privacidad" data-id="element-100">
        <PrivacyContent data-id="element-101" />
      </LegalModal>

      <LegalModal isOpen={activeModal === 'terms'} onClose={() => setActiveModal(null)} title="Términos y Condiciones" data-id="element-102">
        <TermsContent data-id="element-103" />
      </LegalModal>
    </>;
}